import { Link } from 'react-router-dom';
import { FloatGroup } from '../lib/anim/Float';
import { useReveal } from '../lib/anim/useReveal';
import { useCountUp } from '../lib/anim/useCountUp';

function StatTile({
  to,
  label,
  suffix,
  duration,
  staticValue,
  rangeBar,
}: {
  to: number;
  label: string;
  suffix?: string;
  duration?: number;
  staticValue?: string;
  rangeBar?: boolean;
}) {
  const countUp = useCountUp<HTMLSpanElement>(to, { suffix, duration });

  return (
    <div
      className="flex flex-col items-center gap-1 p-7 rounded-md border"
      style={{
        background: 'var(--color-surface)',
        borderColor: 'var(--color-hairline)',
      }}
    >
      <span
        ref={staticValue ? undefined : countUp.ref}
        className="text-2xl md:text-3xl font-mono font-medium tabular-nums"
        style={{ fontFamily: 'var(--font-mono)', color: 'var(--color-ink)' }}
      >
        {staticValue ?? countUp.value}
      </span>
      <span className="text-xs text-center" style={{ color: 'var(--color-mute)', fontFamily: 'var(--font-body)' }}>
        {label}
      </span>
      {rangeBar && (
        <div className="stats-range-bar" aria-hidden>
          <div className="stats-range-bar-fill" />
        </div>
      )}
    </div>
  );
}

export function Hero() {
  const eyebrow = useReveal();
  const headline = useReveal({ delay: 1 });
  const sub = useReveal({ delay: 2 });
  const ctas = useReveal({ delay: 3 });
  const statsStrip = useReveal({ delay: 4 });

  return (
    <section className="relative overflow-hidden py-28 md:py-36" style={{ background: 'var(--color-bg)' }}>
      {/* Ambient float accent — single subtle gold-soft blob */}
      <FloatGroup flat>
        <div
          aria-hidden
          className="pointer-events-none absolute top-16 right-8 md:right-24 w-3 h-3 rounded-full"
          style={{ background: 'var(--color-gold)', opacity: 0.3 }}
        />
      </FloatGroup>

      <div className="max-w-5xl mx-auto px-6 text-center relative z-10">
        {/* Eyebrow */}
        <div ref={eyebrow.ref} {...eyebrow.props} className={eyebrow.props.className}>
          <p
            className="text-xs uppercase mb-6 font-medium"
            style={{
              color: 'var(--color-mute)',
              fontFamily: 'var(--font-mono)',
              letterSpacing: '0.15em',
            }}
          >
            Clarity Platform
          </p>
        </div>

        {/* Headline */}
        <div ref={headline.ref} {...headline.props} className={headline.props.className}>
          <h1
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-ink"
            style={{ fontFamily: 'var(--font-display)', letterSpacing: '-0.02em' }}
          >
            Ship your own micro-SaaS in{' '}
            <span className="relative inline-block whitespace-nowrap" data-r="">
              <span className="relative z-10">30 days.</span>
              {/* Indigo under-band — animates left→right on reveal */}
              <span aria-hidden className="hero-underband" />
            </span>
          </h1>

          <p
            className="mt-5 text-lg md:text-xl font-normal leading-relaxed"
            style={{ color: 'var(--color-mute)', fontFamily: 'var(--font-body)' }}
          >
            Onwards built six. You&rsquo;ll build yours on our rails.
          </p>
        </div>

        {/* CTAs */}
        <div
          ref={ctas.ref}
          {...ctas.props}
          className={`mt-8 flex flex-col sm:flex-row gap-4 justify-center items-center${ctas.props.className ? ' ' + ctas.props.className : ''}`}
        >
          {/* Primary — indigo with gold dot before $149/mo */}
          <Link
            to="/signup?tier=pilot"
            className="btn-primary text-base"
          >
            Start with Pilot —{' '}
            <span
              aria-hidden
              className="inline-block w-1.5 h-1.5 rounded-full mx-2 flex-shrink-0"
              style={{ background: 'var(--color-gold)' }}
            />
            $149/mo
          </Link>
          {/* Ghost secondary */}
          <Link
            to="/stack"
            className="btn-ghost text-base"
          >
            See the stack
          </Link>
        </div>

        {/* Inline stats trust strip — 4 white cards, simultaneous reveal */}
        <div
          ref={statsStrip.ref}
          {...statsStrip.props}
          className={`mt-12 max-w-2xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-3${statsStrip.props.className ? ' ' + statsStrip.props.className : ''}`}
        >
          <StatTile to={6} label="engines" duration={800} />
          <StatTile to={30} label="days" duration={1000} />
          <StatTile to={12} label="yrs operator exp." duration={800} />
          {/* $1M–$50M tile — range bar animates instead of count-up */}
          <StatTile
            to={0}
            label="business size"
            staticValue="$1M–$50M"
            rangeBar
          />
        </div>
      </div>
    </section>
  );
}
